import Asset, {
  AssetCategory,
  AssetIncomeUnits,
  AssetType,
  AssetTypeDef,
  getAssetTypeDef,
  getAssetTypeLabel,
  inheritedYearLimits,
  inheritedYearProblem,
  listAssetTypes,
  Risk
} from "../../../model/Asset";
import {
  Box,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  Typography,
  useTheme
} from "@mui/material";
import React, {useEffect, useState} from "react"
import TextFieldValidator from "../../../components/form/TextFieldValidator";
import {
  getISODateFromDate,
  humanizeString,
  isoToLocalDate,
  moneyToNumberFormat,
  numberToMoneyFormat,
  numberToPercentFormat,
  percentToNumberFormat,
} from "../../../stores/StoreUtilities";
import {
  AssetIncomeDetailInput,
  AssetIncomeInput,
  CreateAssetInput,
  CreateIncomeInput,
  UpdateAssetInput,
  UpdateIncomeInput,
  UserType
} from "../../../API";
import {useResources} from "../../../stores/ResourceProvider";
import Model from "../../../model/Model";
import DatePickerValidator from "../../../components/form/DatePickerValidator";
import FormGroupSpacer from "../../../components/form/FormGroupSpacer";
import Snapshot, {ISnapshotDetail} from "../../../model/Snapshot";
import MilestoneDateFieldValidator from "../../../components/form/MilestoneDateFieldValidator";
import {compareAsc} from "date-fns";
import ModelEditDialog, {ModelEditDialogSize} from "../../../components/model/ModelEditDialog";
import {isNumber} from "../../../model/ModelUtilities";
import Tracking from "../../../components/Tracking";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import IModelItem from "../../../model/IModelItem";
import FormValidator from "../../../components/form/FormValidator";
import DialogButton from "../../../components/form/DialogButton";
import Income, {IncomeType} from "../../../model/Income";
import Schedule from "../../../model/Schedule";
import {managedRiderIncomes, riderIncomeId, riderMonthlyPayment, riderPairProblem} from "../../../components/calculator/AnnuityRider";
import ChatbotOpenImg from "../../../images/ChatbotOpen.png";
import {GrowthModel} from "../../../model/GrowthStrategy";

const RiderIncomeNotSaved = "The asset was saved, but its rider income wasn't. Save again to finish."
const RiderIncomeDuplicates = "This annuity has more than one rider income. Choose the one to keep first."

export class IncomeDetailEdit {
  incomeType: IncomeType
  amount: string
  rate: string

  constructor (data: any, units: AssetIncomeUnits) {
    this.incomeType = data.incomeType ?? IncomeType.OtherIncome
    this.amount = isNumber(data.amount) ? numberToMoneyFormat(data.amount, 0) : ""
    this.rate = isNumber(data.rate) ? numberToPercentFormat(data.rate, 2) : ""
  }
}

export const PayoutSingleLife = "Single life"
export const PayoutJointLife = "Joint life (100% to survivor)"

const DetailDescriptions: string[] = [
  "Enter the dollar amounts of each type of income from your most recent 1099 statements. The relative percentages will be used to project the income in future years.",
  "Enter the return rate % for each type of income to enable calculating of taxable income from interest and dividends.  Enter Capital Growth and Total Cost Basis to enable calculating Capital Gains.",
  // "Enter the relative percentage of the total return for each type of income.",
]

const AssetEditDialog = ({
  open,
  asset,
  model,
  snapshot,
  onClose,
  onSave,
  onDelete,
  onArchive,
  quickForm
}: {
  open?: boolean
  asset: Asset
  model: Model
  snapshot?: Snapshot
  onClose?(): void
  onSave?(update: Asset, snapshotItem?: ISnapshotDetail): void
  onDelete?(asset: Asset): void
  onArchive?(asset: Asset): void
  quickForm?: boolean
}) => {
  const [assetModel, setAssetModel] = useState<Asset>(asset)
  const [snapshotDetail, setSnapshotDetail] = useState<ISnapshotDetail | undefined>()
  const [description, setDescription] = useState<string>("")
  const [assetCategory, setAssetCategory] = useState<AssetCategory | undefined>()
  const [assetType, setAssetType] = useState<AssetType | undefined>()
  const [assetTypeDef, setAssetTypeDef] = useState<AssetTypeDef | undefined>()
  const [owner, setOwner] = useState<string | undefined>()
  const [risk, setRisk] = useState<Risk | undefined>()
  const [returnRate, setReturnRate] = useState<string | undefined>()
  const [minReturnRate, setMinReturnRate] = useState<number>(0)
  const [rateLock, setRateLock] = useState<"Model" | "User" | "Asset" | undefined>()
  const [withdrawalOrder, setWithdrawalOrder] = useState<string | undefined>()
  const [withdrawalOrderPlaceholder, setWithdrawalOrderPlaceholder] = useState<string>("")
  const [withdrawalOrderLock, setWithdrawalOrderLock] = useState<boolean>(false)
  const [balance, setBalance] = useState<string | undefined>()
  const [balanceDate, setBalanceDate] = useState<Date | undefined>()
  const [priorYear, setPriorYear] = useState<number>(0)
  const [priorEoYBalance, setPriorEoYBalance] = useState<string | undefined>()
  const [withdrawalsYTD, setWithdrawalsYTD] = useState<string | undefined>()
  const [start, setStart] = useState<string>()
  const [end, setEnd] = useState<string>()
  const [inheritedYear, setInheritedYear] = useState<string | undefined>()
  const [originalOwnerBirthYear, setOriginalOwnerBirthYear] = useState<string | undefined>()
  const [managed, setManaged] = useState<boolean>(false)
  const [netWorthExcluded, setNetWorthExcluded] = useState<boolean>(false)
  const [riderAnnualIncome, setRiderAnnualIncome] = useState<string | undefined>()
  const [riderStartDate, setRiderStartDate] = useState<Date | undefined>()
  const [riderJointLife, setRiderJointLife] = useState<boolean>(false)
  const [linkedRiderIncome, setLinkedRiderIncome] = useState<Income | undefined>()
  const [riderCandidates, setRiderCandidates] = useState<Income[]>([])
  const [riderPairIssue, setRiderPairIssue] = useState<string | undefined>()
  const [archived, setArchived] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [size, setSize] = useState<ModelEditDialogSize>("xs")
  const [notes, setNotes] = useState<string | undefined>()
  const [assetIncomeEnabled, setAssetIncomeEnabled] = useState<boolean>(false)
  // const [assetIncomeDate, setAssetIncomeDate] = useState<string | undefined>()
  const [incomeUnits, setIncomeUnits] = useState<AssetIncomeUnits>(AssetIncomeUnits.NominalRate)
  const [incomeBasis, setIncomeBasis] = useState<string | undefined>()
  const [incomeDetails, setIncomeDetails] = useState<IncomeDetailEdit[]>([
    new IncomeDetailEdit({
      incomeType: IncomeType.NonqualifiedDividends,
      amount: null,
      rate: null
    }, AssetIncomeUnits.NominalRate),
    new IncomeDetailEdit({
      incomeType: IncomeType.QualifiedDividends,
      amount: null,
      rate: null
    }, AssetIncomeUnits.NominalRate),
    new IncomeDetailEdit({
      incomeType: IncomeType.TaxableInterest,
      amount: null
    }, AssetIncomeUnits.NominalRate),
    // new AccountHoldingEdit({
    //   incomeType: IncomeType.ShortTermCapitalGainsOrLoss,
    // amount: 0
    // }, AssetIncomeUnits.NominalRate),
    new IncomeDetailEdit({
      incomeType: IncomeType.LongTermCapitalGainsOrLoss,
      amount: null,
      rate: null
    }, AssetIncomeUnits.NominalRate)
  ])
  const [isLocked, setIsLocked] = useState<boolean>(false)
  const [totalIncome, setTotalIncome] = useState<number>(0)
  const [totalReturnRate, setTotalReturnRate] = useState<number>(0)

  const { modelStore, userStore, notify, confirm } = useResources()
  const theme = useTheme()

  const styles = {
    formLabel: {
      fontSize: 14,
      fontWeight: 400,
      color: theme.palette.grey["700"],
    },
    sectionLabel: {
      fontSize: 14,
      fontWeight: 700,
      color: theme.palette.grey["700"],
      bgcolor: theme.palette.grey["300"],
    }
  }

  useEffect(() => {
    setAssetModel(asset)
    setDescription(asset.description)
    setAssetCategory(asset.assetCategory)
    setAssetType(asset.assetType)
    setAssetTypeDef(asset.assetTypeDef)
    let person = asset.ownerId ? model.getPerson(asset.ownerId) : undefined
    setOwner(person ? person.nickname : model.getDefaultPersonNickname(!asset.assetTypeDef.isIRA && !asset.assetTypeDef.hasIncomeRider))
    setRisk(asset.risk ?? Risk.Low)
    setReturnRate(isNumber(asset.returnRate) ? numberToPercentFormat(asset.returnRate, 2) : "")
    if (asset.rateLock) {
      setRateLock("User")
    } else if (model.growthStrategy && model.growthStrategy.ignoreCustomGrowthRates) {
      setRateLock("Model")
      if (model.growthStrategy.growthModel === GrowthModel.Simple) {
        setReturnRate(numberToPercentFormat(model.growthStrategy.growthRate, 2))
      }
    }
    setWithdrawalOrder(asset.withdrawalOrder ? asset.withdrawalOrder.toString() : "")
    setWithdrawalOrderPlaceholder(getAssetTypeDef(asset.assetType).withdrawalOrder.toString())
    setWithdrawalOrderLock(model.hasLock(asset.id, "withdrawalOrder"))
    const itemBalance = modelStore.getNetWorthItemBalanceDetail(asset, model, snapshot)
    setSnapshotDetail(itemBalance.detail)
    setBalance(numberToMoneyFormat(itemBalance.balance, 0))
    setBalanceDate((itemBalance.balanceDate))
    setPriorYear((new Date()).getFullYear() - 1)
    setPriorEoYBalance(asset.priorEoYBalance ? numberToMoneyFormat(asset.priorEoYBalance, 0) : undefined)
    setWithdrawalsYTD(asset.withdrawalsYTD ? numberToMoneyFormat(asset.withdrawalsYTD, 0) : undefined)
    setInheritedYear(asset.inheritedYear ? asset.inheritedYear.toString() : "")
    setOriginalOwnerBirthYear(asset.originalOwnerBirthYear ? asset.originalOwnerBirthYear.toString() : "")
    setStart(asset.start)
    setEnd(asset.end)
    setManaged(asset.managed)
    setNetWorthExcluded(asset.netWorthExcluded)
    // The managed rider income: the single active one, else a single archived one to restore. Several candidates
    // are a repair the user makes explicitly, never a first-match guess.
    const candidates = managedRiderIncomes(model, asset)
    const activeCandidates = candidates.filter((i: Income) => !i.archived)
    const linkedIncome = activeCandidates.length === 1 ? activeCandidates[0] :
      (activeCandidates.length === 0 && candidates.length === 1 ? candidates[0] : undefined)
    setLinkedRiderIncome(linkedIncome)
    setRiderCandidates(linkedIncome ? [] : (activeCandidates.length > 1 ? activeCandidates : candidates))
    setRiderPairIssue(asset.createdAt ? riderPairProblem(model, asset) : undefined)
    setRiderAnnualIncome(linkedIncome ? numberToMoneyFormat(linkedIncome.amount * 12, 0) : "")
    setRiderStartDate(linkedIncome && linkedIncome.start ? model.getDate(linkedIncome.start) : undefined)
    setRiderJointLife(linkedIncome ? linkedIncome.survivorPercent === 100 : false)
    setArchived(asset.archived)
    setAssetIncomeEnabled(model.growthStrategy.assetIncomeEnabled ?? false)
    const assetIncome = asset.assetIncome
    if (model.growthStrategy.assetIncomeEnabled && (assetIncome || assetType === AssetType.Brokerage)) {
      if (assetIncome) {
        setIncomeBasis(assetIncome.basis ? numberToMoneyFormat(assetIncome.basis, 0) : "$0")
        const units = /* assetIncome.units ?? */ AssetIncomeUnits.NominalRate
        setIncomeUnits(units)
        if (assetIncome.details && assetIncome.details.length > 0) {
          const interestDetail = asset.getAssetIncomeDetail(IncomeType.TaxableInterest)
          const qualifiedDividendsDetail = asset.getAssetIncomeDetail(IncomeType.QualifiedDividends)
          const nonQualifiedDividendsDetail = asset.getAssetIncomeDetail(IncomeType.NonqualifiedDividends)
          const capitalGainsDetail = asset.getAssetIncomeDetail(IncomeType.LongTermCapitalGainsOrLoss)

          setIncomeDetails([new IncomeDetailEdit(interestDetail, units), new IncomeDetailEdit(nonQualifiedDividendsDetail, units), new IncomeDetailEdit(qualifiedDividendsDetail, units), new IncomeDetailEdit(capitalGainsDetail, units)])
        }
      } else {
        setIncomeBasis(numberToMoneyFormat(0, 0))
        const units = /* assetIncome.units ?? */ AssetIncomeUnits.NominalRate
        setIncomeUnits(units)
        const interestDetail = asset.getAssetIncomeDetail(IncomeType.TaxableInterest)
        const qualifiedDividendsDetail = asset.getAssetIncomeDetail(IncomeType.QualifiedDividends)
        const nonQualifiedDividendsDetail = asset.getAssetIncomeDetail(IncomeType.NonqualifiedDividends)
        const capitalGainsDetail = asset.getAssetIncomeDetail(IncomeType.LongTermCapitalGainsOrLoss)
        capitalGainsDetail.rate = asset.returnRate ?? 0
        setIncomeDetails([new IncomeDetailEdit(interestDetail, units), new IncomeDetailEdit(nonQualifiedDividendsDetail, units), new IncomeDetailEdit(qualifiedDividendsDetail, units), new IncomeDetailEdit(capitalGainsDetail, units)])
      }
      if (!model.growthStrategy || !model.growthStrategy.ignoreCustomGrowthRates) {
        setRateLock("Asset")
      }
    }
    // setAssetIncomeDate(asset.assetIncomeDate)
    setIsLocked(model.hasLock(asset.id, "*") || model.isLocked())
    setNotes(asset.notes)
    setIsOpen(open === true)
    if (model.growthStrategy.assetIncomeEnabled && asset.assetType === AssetType.Brokerage) {
      setSize("md")
    }
  }, [asset, model, snapshot, open])

  const getIncomeTotal = () : number => {
    let incomeTotal = 0
    // Ignore Qualified Dividends
    if (incomeUnits === AssetIncomeUnits.NominalDollars) {
      incomeTotal = incomeDetails.reduce((total: number, detail: IncomeDetailEdit) => detail.incomeType !== IncomeType.QualifiedDividends ? total + moneyToNumberFormat(detail.amount) : total, 0)
    } else {
      incomeTotal = incomeDetails.reduce((total: number, detail: IncomeDetailEdit) => detail.incomeType !== IncomeType.QualifiedDividends ? total + percentToNumberFormat(detail.rate) : total, 0)
    }
    return incomeTotal
  }

  const getIncomeReturnRate = () : string => {
    const valueFound = incomeDetails.find((incomeDetail: IncomeDetailEdit) => incomeDetail.rate !== "")
    if (!valueFound) {
      return ""
    } else {
      let returnRateTotal = 0
      returnRateTotal = incomeDetails.reduce((total: number, detail: IncomeDetailEdit) => total + percentToNumberFormat(detail.rate), 0)
      return numberToPercentFormat(returnRateTotal, 2)
    }
  }

  useEffect(() => {
    if (assetType === AssetType.Brokerage) {
      const income = getIncomeTotal()
      setTotalIncome(income)
      const incomeReturnRate = getIncomeReturnRate()
      if (rateLock !== "Model") {
        if (incomeReturnRate !== "") {
          setReturnRate(incomeReturnRate)
          setRateLock("Asset")
        } else {
          setRateLock(undefined)
        }
      }
    }
  }, [balance, returnRate, incomeDetails])

  const handleClose = async (event: any) => {
    if (onClose) {
      onClose()
    }
  }

  // Create or update the managed rider Income for a saved rider asset. The linked income is the single source of truth
  // for the rider's amount, start date and survivor treatment. Throws unless the server confirms a record linked to this
  // asset; a create whose result was lost is found again by its fixed id and finished as an update.
  const saveRiderIncome = async (savedAsset: Asset) => {
    const person = model.getPersonByNickname(owner)
    const annualAmount = moneyToNumberFormat(riderAnnualIncome, 0) ?? 0
    const fields = {
      accountId: savedAsset.accountId,
      userId: savedAsset.userId,
      modelId: savedAsset.modelId,
      description: `${description} Rider Income`,
      amount: riderMonthlyPayment(annualAmount),
      incomeType: savedAsset.assetTypeDef.taxFree ? IncomeType.PensionsAndAnnuitiesNonTaxable : IncomeType.PensionsAndAnnuitiesTaxable,
      schedule: JSON.stringify(Schedule.getDefaultSchedule("Monthly")),
      start: getISODateFromDate(riderStartDate!),
      end: null,
      annualInf: 0,
      infLock: true,
      survivorPercent: riderJointLife ? 100 : 0,
      ownerId: person ? person.id : null,
      assetId: savedAsset.id
    }
    let saved: Income | undefined
    if (linkedRiderIncome) {
      saved = await modelStore.updateIncome({id: linkedRiderIncome.id, ...fields, archived: false})
    } else {
      const id = riderIncomeId(savedAsset)
      saved = await modelStore.createIncome({id, ...fields, sortOrder: 0})
      if (!saved && await modelStore.getIncome(id)) {
        saved = await modelStore.updateIncome({id, ...fields, archived: false})
      }
    }
    if (!saved || saved.assetId !== savedAsset.id || saved.modelId !== savedAsset.modelId) {
      throw new Error(RiderIncomeNotSaved)
    }
    setLinkedRiderIncome(saved)
  }

  // Delete a managed rider income. One that is already gone counts only when a read confirms it (a failed read
  // throws), so a failed delete never leaves an income budgeted without its asset.
  const removeRiderIncome = async (income: Income): Promise<boolean> => {
    if (await modelStore.deleteIncome(income.id).catch(() => undefined)) {
      return true
    }
    if (await modelStore.getIncome(income.id)) {
      return false
    }
    await modelStore.deleteModelItem("incomes", income.id)
    return true
  }

  // Explicit repair when an annuity has several rider incomes: keep the chosen one and delete the others
  const keepRiderIncome = async (keep: Income) => {
    try {
      for (const income of riderCandidates.filter((i: Income) => i.id !== keep.id)) {
        if (!(await removeRiderIncome(income))) {
          throw new Error("A duplicate rider income couldn't be deleted. Try again.")
        }
      }
      setLinkedRiderIncome(keep)
      setRiderCandidates([])
      setRiderAnnualIncome(numberToMoneyFormat(keep.amount * 12, 0))
      setRiderStartDate(keep.start ? model.getDate(keep.start) : undefined)
      setRiderJointLife(keep.survivorPercent === 100)
      setRiderPairIssue(riderPairProblem(model, assetModel))
    } catch (err: any) {
      notify.show('error', err.message)
    }
  }

  // Several active rider incomes: the one to keep is chosen before the asset leaves the rider type or is archived
  const hasDuplicateRiderIncomes = () => riderCandidates.filter((i: Income) => !i.archived).length > 1

  const validateRiderFields = () : string | undefined => {
    if (!assetTypeDef?.hasIncomeRider) {
      return undefined
    }
    const annualAmount = moneyToNumberFormat(riderAnnualIncome, 0) ?? 0
    if (annualAmount <= 0) {
      return "Guaranteed Annual Income must be greater than $0"
    }
    if (!riderStartDate) {
      return "Income Start Date is required"
    }
    const person = model.getPersonByNickname(owner)
    if (!person) {
      return "An Owner is required for an annuity with an income rider"
    }
    if (person.lifeExpectancyDate && compareAsc(riderStartDate, isoToLocalDate(person.lifeExpectancyDate)) >= 0) {
      return "Income Start Date must be before the owner's life expectancy"
    }
    // The projection resets the balance of assets with no acquisition date each year,
    // which would silently disable the rider drawdown — require it for rider types
    const assetStartDate = model.getDate(start)
    if (!assetStartDate) {
      return "Acquisition Date is required for an annuity with an income rider"
    }
    if (compareAsc(riderStartDate, assetStartDate) < 0) {
      return "Income Start Date cannot be before the Acquisition Date"
    }
    return undefined
  }

  const handleSave = async (event: any) => {
    try {
      // Several rider incomes leave the rider fields empty; ask for the explicit choice before validating them. Several
      // active ones also block a change to an ordinary type, which would otherwise leave them all budgeted
      if ((assetTypeDef?.hasIncomeRider && riderCandidates.length > 1) || hasDuplicateRiderIncomes()) {
        notify.show('error', RiderIncomeDuplicates)
        return
      }
      const riderError = validateRiderFields()
      if (riderError) {
        notify.show('error', riderError)
        return
      }
      // A saved rider changing to an ordinary type: archive its income first, so a failed asset save leaves a rider
      // without income (blocked and repairable) instead of an income budgeted for an ordinary asset
      if (assetModel.createdAt && assetModel.assetTypeDef.hasIncomeRider && !assetTypeDef?.hasIncomeRider &&
          linkedRiderIncome && !linkedRiderIncome.archived) {
        if (!(await modelStore.updateIncome({id: linkedRiderIncome.id, archived: true}))) {
          throw new Error("The rider income couldn't be archived, so the asset wasn't changed. Try again.")
        }
      }
      const person = model.getPersonByNickname(owner)
      let updatedAsset: Asset | undefined
      let updatedSnapshotDetail: ISnapshotDetail | undefined
      const balanceValue = moneyToNumberFormat(balance, 0) ?? 0
      const priorBalanceValue = moneyToNumberFormat(priorEoYBalance, 0) ?? null
      const withdrawalsYTDValue = moneyToNumberFormat(withdrawalsYTD, 0) ?? null

      let assetIncomeInput : AssetIncomeInput | null = null

      if (assetIncomeEnabled && assetType === AssetType.Brokerage) {
        const incomeReturnRate = getIncomeReturnRate()
        if (incomeReturnRate !== "") {
          assetIncomeInput = {
            basis: moneyToNumberFormat(incomeBasis, 0),
            rateTotal: 0,
            units: incomeUnits,
            details: []
          }
          const interestDetail = getAssetIncomeDetail(IncomeType.TaxableInterest)
          const qualifiedDividendsDetail = getAssetIncomeDetail(IncomeType.QualifiedDividends)
          const nonQualifiedDividendsDetail = getAssetIncomeDetail(IncomeType.NonqualifiedDividends)
          const capitalGainsDetail = getAssetIncomeDetail(IncomeType.LongTermCapitalGainsOrLoss)
          assetIncomeInput.details = [interestDetail, qualifiedDividendsDetail, nonQualifiedDividendsDetail, capitalGainsDetail]
          assetIncomeInput.rateTotal = interestDetail.rate! + qualifiedDividendsDetail.rate! + nonQualifiedDividendsDetail.rate! + capitalGainsDetail.rate!
        }
      }

      if (!assetModel.createdAt) {
        const input: CreateAssetInput = {
          id: assetModel.id,
          accountId: assetModel.accountId,
          userId: assetModel.userId,
          modelId: assetModel.modelId,
          description: description,
          assetCategory: assetCategory,
          assetType: assetType,
          ownerId: person ? person.id : null,
          risk: risk,
          balance: balanceValue,
          balanceDate: balanceDate ? getISODateFromDate(balanceDate) : null,
          priorEoYBalance: priorBalanceValue,
          withdrawalsYTD: withdrawalsYTDValue,
          withdrawalOrder: withdrawalOrder ? parseInt(withdrawalOrder) : null,
          returnRate: returnRate !== undefined && returnRate >= "0" ? parseFloat(returnRate) / 100 : null,
          rateLock: rateLock === "User",
          inheritedYear: assetTypeDef && assetTypeDef.requireExtraFields && inheritedYear ? parseInt(inheritedYear) : null,
          originalOwnerBirthYear: originalOwnerBirthYear ? parseInt(originalOwnerBirthYear) : null,
          start: start,
          // end: end ?? null,
          managed: managed,
          netWorthExcluded: netWorthExcluded,
          sortOrder: assetModel.sortOrder,
          assetIncome: assetIncomeInput,
          // assetIncomeDate: assetIncomeEnabled ? assetIncomeDate : null,
          notes: notes
        }
        if (assetIncomeInput) {
          input.returnRate = assetIncomeInput.rateTotal
        } else if (rateLock === "Model") {
          delete input.returnRate
        }
        updatedAsset = await modelStore.createAsset(input)
        if (!updatedAsset && assetTypeDef?.hasIncomeRider && await modelStore.getAsset(assetModel.id)) {
          // The create's result was lost but the asset exists: finish as an update of that same asset
          updatedAsset = await modelStore.updateAsset(input as UpdateAssetInput)
        }
        Tracking.event({action: "Asset Created"})
      } else  {
        const input: UpdateAssetInput = {
          id: assetModel.id,
          accountId: assetModel.accountId,
          userId: assetModel.userId,
          modelId: assetModel.modelId,
          description: description,
          assetCategory: assetCategory,
          assetType: assetType,
          ownerId: person ? person.id : null,
          risk: risk,
          balance: balanceValue,
          balanceDate: snapshot ? getISODateFromDate(snapshot.date) : null,
          priorEoYBalance: priorBalanceValue,
          withdrawalsYTD: withdrawalsYTDValue,
          withdrawalOrder: withdrawalOrder ? parseInt(withdrawalOrder) : null,
          returnRate: returnRate !== undefined && returnRate >= "0" ? parseFloat(returnRate) / 100 : null,
          rateLock: rateLock === "User",
          inheritedYear: assetTypeDef && assetTypeDef.requireExtraFields && inheritedYear ? parseInt(inheritedYear) : null,
          originalOwnerBirthYear: originalOwnerBirthYear ? parseInt(originalOwnerBirthYear) : null,
          start: start,
          // end: end ?? null,
          managed: managed,
          netWorthExcluded: netWorthExcluded,
          assetIncome: assetIncomeInput,
          // assetIncomeDate: assetIncomeEnabled ? assetIncomeDate : null,
          notes: notes
        }
        if (withdrawalOrderLock) {
          delete input.withdrawalOrder
        }
        if (assetIncomeInput) {
          input.returnRate = assetIncomeInput.rateTotal
        } else if (rateLock === "Model") {
          delete input.returnRate
        }

        // if (snapshot && compareAsc(snapshot.date, assetModel.balanceDate) ) {
        //   // Don't update the balance if the snapshot date doesn't match the asset balanceDate
        //   delete input.balance
        //   delete input.balanceDate
        // }

        updatedAsset = await modelStore.updateAsset(input)
        Tracking.event({action: "Asset Updated"})
      }

      // Only update the snapshot if the start date <= the snapshot date
      const startDate = model.getDate(start)
      if ((!startDate && snapshot && snapshot.isCurrent) ||
          (startDate && snapshot && compareAsc(startDate, snapshot.date) <= 0)) {
        updatedSnapshotDetail = {
          id: assetModel.id,
          typename: "Asset",
          balance: balanceValue
        }
      }

      if (updatedAsset && updatedAsset.assetTypeDef.hasIncomeRider) {
        // A retry after a failed rider income write updates this asset instead of creating another
        setAssetModel(updatedAsset)
        try {
          await saveRiderIncome(updatedAsset)
        } catch (err) {
          // An owner or account-type change that only half-saved: put the asset back so the pair stays consistent
          if (linkedRiderIncome && (assetModel.ownerId !== updatedAsset.ownerId || assetModel.assetType !== updatedAsset.assetType)) {
            await modelStore.updateAsset({id: updatedAsset.id, ownerId: assetModel.ownerId ?? null, assetType: assetModel.assetType})
              .catch(() => undefined)
          }
          throw err
        }
        setRiderCandidates([])
        setRiderPairIssue(undefined)
      } else if (!updatedAsset && assetTypeDef?.hasIncomeRider) {
        throw new Error("The asset wasn't saved. Try again.")
      }

      if (onSave && updatedAsset) {
        onSave(updatedAsset, updatedSnapshotDetail)
      } else if (onClose) {
        onClose()
      }
    } catch (err: any) {
      notify.show('error', err.message)
    }
  }

  const renderReferences = (refs: IModelItem[]) => {
    return (
      <ul>
        {refs.map((ref: IModelItem) =>
          <li id={ref.id}>
            {ref.description} <em>{ref.getTypename()}</em>
          </li>
        )}
      </ul>
    )
  }

  const handleDelete = async () => {
    if (onDelete) {
      const refs = modelStore.getReferences(model, assetModel).filter((item: IModelItem) => !item.archived)
      const content = <Box mt={1}>
        Are you sure you want to delete this item? It will be permanently deleted from all snapshots. Alternatively, you can archive it.
        {refs.length > 0 &&
          <Box mt={1}>
            <Typography variant="h6">Affected Budget Items</Typography>
            {renderReferences(refs)}
          </Box>
        }
      </Box>
      confirm.show('Confirm Delete', content,
        ['Delete', 'Cancel'],
        async () => {
          if (assetModel.createdAt) {
            // Delete the rider income first, so a failure keeps the asset rather than leaving its income behind. A rider
            // income whose create result and read-back were both lost isn't in the model, so look for it at its fixed id
            try {
              const incomes = managedRiderIncomes(model, assetModel)
              if (assetModel.assetTypeDef.hasIncomeRider && !incomes.some((i: Income) => i.id === riderIncomeId(assetModel))) {
                const lost = await modelStore.getIncome(riderIncomeId(assetModel))
                if (lost) incomes.push(lost)
              }
              for (const income of incomes) {
                if (!(await removeRiderIncome(income))) {
                  notify.show('error', "The rider income couldn't be deleted, so the asset was kept. Try again.")
                  return true
                }
              }
            } catch (err: any) {
              notify.show('error', "The rider income couldn't be confirmed as deleted, so the asset was kept. Try again.")
              return true
            }
            const deleted = await modelStore.deleteAsset(assetModel.id)
            if (deleted) {
              onDelete(deleted)
            }
          } else {
            onDelete(assetModel)
          }
          return true
        })
    }
  }

  const handleArchive = async () => {
    if (onArchive) {
      if (!archived) {
        if (hasDuplicateRiderIncomes()) {
          notify.show('error', RiderIncomeDuplicates)
          return
        }
        const refs = modelStore.getReferences(model, assetModel).filter((item: IModelItem) => !item.archived)
        const content = <Box mt={1}>
          Are you sure you want to archive this item?
          {refs.length > 0 &&
            <Box mt={1}>
              <Typography variant="h6">Affected Budget Items</Typography>
              {renderReferences(refs)}
            </Box>
          }
        </Box>
        confirm.show('Confirm Archive', content,
          ['Archive', 'Cancel'],
          async () => {
            if (assetModel.createdAt) {
              // Archive the rider income before the asset, so a failure never leaves it budgeted for an archived asset
              if (linkedRiderIncome && !linkedRiderIncome.archived &&
                  !(await modelStore.updateIncome({id: linkedRiderIncome.id, archived: true}).catch(() => undefined))) {
                notify.show('error', "The rider income couldn't be archived, so the asset was left as it was. Try again.")
                return true
              }
              const asset = await modelStore.updateAsset({id: assetModel.id, archived: !assetModel.archived})
              if (asset) {
                onArchive(asset)
              }
            } else if (onDelete) {
              onDelete(assetModel)
            }
            return true
          })
      } else {
        // Restore the asset before its rider income, so the income is never active for an archived asset
        const asset = await modelStore.updateAsset({id: assetModel.id, archived: !assetModel.archived})
        const archivedIncomes = managedRiderIncomes(model, assetModel).filter((i: Income) => i.archived)
        if (asset && archivedIncomes.length === 1 &&
            !(await modelStore.updateIncome({id: archivedIncomes[0].id, archived: false}).catch(() => undefined))) {
          notify.show('error', "The asset was restored, but its rider income wasn't. Open the asset and Save to finish.")
        }
        if (asset) {
          onArchive(asset)
        }
      }
    }
  }

  const handleRateLock = () => {
    if (rateLock === "User") {
      if (model.getOverrideGrowthLock()) {
        setRateLock("Model")
        setReturnRate(numberToPercentFormat(model.getDefaultGrowthRate()))
      } else {
        setRateLock(undefined)
      }
    } else if (rateLock === "Model") {
      setRateLock("User")
    } else if (rateLock === undefined) {
      setRateLock("User")
    }
  }

  const renderRateField = () => {
    let endAdornment
    let helperText
    let disabled = false
    if (userStore.isFree) {
      helperText = `Default: ${model.getDefaultGrowthRate()}`
    } else if (rateLock === "User") {
      endAdornment = (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle rate lock"
            onClick={() => handleRateLock()}
            edge="end"
            disabled={isLocked}
          >
            <LockPersonIcon/>
          </IconButton>
        </InputAdornment>
      )
      helperText = "Locked by User"
    } else if (rateLock === "Model") {
      endAdornment = (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle rate lock"
            onClick={() => handleRateLock()}
            edge="end"
            disabled={isLocked}
          >
            <LockIcon/>
          </IconButton>
        </InputAdornment>
      )
      helperText = "Locked by Growth Strategy"
      disabled = true
    } else if (rateLock === "Asset") {
      endAdornment = (
        <InputAdornment position="end">
          <LockIcon/>
        </InputAdornment>
      )
      helperText = "Locked to Income Rates"
      disabled = true
    } else {
      endAdornment = (
        <InputAdornment position="end">
          <IconButton
            aria-label="toggle rate lock"
            onClick={() => handleRateLock()}
            edge="end"
            disabled={isLocked}
          >
            <LockOpenIcon/>
          </IconButton>
        </InputAdornment>
      )
      helperText = `Default: ${numberToPercentFormat(model.getDefaultGrowthRate(), 2)}`
    }

    return (
      <TextFieldValidator
        type="string"
        validators={{ required: false, minValue: minReturnRate }}
        name="returnRate"
        variant="standard"
        margin="dense"
        fullWidth
        label={assetType === AssetType.Brokerage ? "Total Growth Rate %" : "Growth Rate %"}
        value={returnRate}
        InputProps={{
          endAdornment: endAdornment
        }}
        helperText={helperText}
        disabled={disabled}
        onChange={(event: any) => setReturnRate(event.target.value)}
      />
    )
  }

  const renderRiderFields = () => {
    if (!assetTypeDef?.hasIncomeRider) {
      return null
    }
    const person = model.getPersonByNickname(owner)
    const earlyStartWarning = person && riderStartDate &&
      person.getAgeOnDate(riderStartDate) < 59.5
    const repairHint = riderCandidates.length > 1 ? "Choose the rider income to keep." :
      archived ? "Unarchive it to repair it." : "Save to repair it."
    return (
      <Box>
        {riderPairIssue &&
          <Typography variant="caption" sx={{display: "block", color: "warning.main", pl: 0.5}}>
            This annuity {riderPairIssue}. {repairHint}
          </Typography>
        }
        {riderCandidates.length > 1 && riderCandidates.map((income: Income) =>
          <Box key={income.id} display="flex" alignItems="center" justifyContent="space-between" pl={0.5}>
            <Typography variant="caption">
              {numberToMoneyFormat(income.amount * 12, 0)}/yr from {income.start}{income.archived ? " (archived)" : ""}
            </Typography>
            <DialogButton type="button" variant="secondary" size="small" disabled={isLocked} onClick={() => keepRiderIncome(income)}>
              Keep this one
            </DialogButton>
          </Box>
        )}
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <TextFieldValidator
            type="string"
            validators={{required: true, isMoney: true}}
            required
            name="riderAnnualIncome"
            variant="standard"
            margin="dense"
            fullWidth
            label="Guaranteed Annual Income"
            value={riderAnnualIncome}
            disabled={isLocked}
            onChange={(event: any) => setRiderAnnualIncome(event.target.value)}
          />
          <FormGroupSpacer/>
          <Box pt={1} width="100%">
            <DatePickerValidator
              name="riderStartDate"
              label="Income Start Date"
              value={riderStartDate}
              format="MM-dd-yyyy"
              variant="standard"
              margin="dense"
              fullWidth
              required
              disabled={isLocked}
              onChange={(newValue: any) => setRiderStartDate(newValue)}
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Box width="50%">
            <TextFieldValidator
              type="text"
              validators={{required: true}}
              required
              name="riderPayout"
              label="Payout"
              variant="standard"
              margin="dense"
              fullWidth
              helperText={model.persons.length <= 1 ? "Joint life needs a second person on the plan" : undefined}
              autocompleteOptions={{
                freeSolo: false,
                disableClearable: true,
                options: [PayoutSingleLife, PayoutJointLife],
                getOptionDisabled: (option: string) => option === PayoutJointLife && model.persons.length <= 1,
                value: riderJointLife ? PayoutJointLife : PayoutSingleLife,
                disabled: isLocked,
                onChange: (event: any, value: string, reason: any) => {
                  setRiderJointLife(value === PayoutJointLife)
                }
              }}
            />
          </Box>
          <FormGroupSpacer/>
          <Box width="50%"/>
        </Box>
        {earlyStartWarning &&
          <Typography variant="caption" sx={{display: "block", color: "warning.main", pl: 0.5}}>
            Income starting before age 59½ may be subject to a 10% early withdrawal penalty, which is not modeled.
          </Typography>
        }
        <Typography variant="caption" sx={{display: "block", color: "text.secondary", fontStyle: "italic", pl: 0.5}}>
          {assetType === AssetType.QualifiedAnnuityWithIncomeRider && "This account type represents an IRA-owned annuity. Payments count toward the owner's eligible IRA RMDs. "}
          A matching income is added to your Budget automatically when payments begin.
          The account is excluded from other withdrawals once income starts. Do not include rider
          payments in YTD Withdrawals.
        </Typography>
      </Box>
    )
  }

  if (!assetModel || !isOpen) {
    return null
  }

  const isAdvisorOrAdmin = userStore.isAdvisorOrAdmin
  const isClientAccount = modelStore.currentUser ? modelStore.currentUser.userType === UserType.Client : false
  const balanceLabel = (assetCategory === AssetCategory.LiquidInvestableAssets) ? "Balance" : "Value"
  const requireExtraFields = assetTypeDef && assetTypeDef.requireExtraFields
  const managedCategory = (assetCategory === AssetCategory.LiquidInvestableAssets || assetCategory === AssetCategory.OtherAssets)
  const currentYear = new Date().getFullYear()
  // The year of death must fit the inherited type; a saved one that doesn't (or is missing) shows why until it's fixed
  const inheritedYearRange = assetType !== undefined ? inheritedYearLimits(assetType, currentYear) : {min: 1900, max: currentYear}
  const inheritedYearMessage = requireExtraFields && assetType !== undefined && (inheritedYear || assetModel.createdAt) ?
    inheritedYearProblem(assetType, inheritedYear ? parseInt(inheritedYear) : undefined, currentYear) : undefined

  if (quickForm) {
    return (
      <FormValidator id="asset-quick-form" onSubmit={handleSave}>
        <Paper elevation={1} sx={{m:0, mb:2, px:2, py:1}}>
          <TextFieldValidator
            margin="normal"
            name="description"
            label="Account Name"
            type="text"
            fullWidth
            variant="standard"
            required
            autoFocus
            validators={{ required: true }}
            value={description}
            onChange={(event: any) => setDescription(event.target.value)}
          />
          {assetCategory === AssetCategory.LiquidInvestableAssets && assetType !== AssetType.Brokerage &&
            <TextFieldValidator
              type="text"
              validators={{ required: true }}
              required
              name="assetType"
              label="Account Type"
              variant="standard"
              autocompleteOptions={{
                freeSolo: false,
                options: listAssetTypes(),
                getOptionLabel: (option: number) => {
                  return(getAssetTypeLabel(option))
                },
                value: assetType,
                onChange: (event: any, value: number, reason: any) => {
                  setAssetType(value)
                  const typeDef = getAssetTypeDef(value)
                  setAssetTypeDef(typeDef)
                  if (typeDef && (typeDef.isIRA || typeDef.hasIncomeRider) && owner && owner === Model.jointNickname) {
                    setOwner(undefined)
                  }
                  if (typeDef?.hasRMD || typeDef?.hasIncomeRider) {
                    setNetWorthExcluded(false)
                  }
                }
              }}
            />
          }
          {renderRiderFields()}
          {requireExtraFields &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <TextFieldValidator
                key={assetType}
                type="number"
                validators={{required: requireExtraFields, isInt: true, minValue: inheritedYearRange.min, maxValue: inheritedYearRange.max}}
                errorMessages={{minValue: inheritedYearMessage, maxValue: inheritedYearMessage}}
                name="inheritedYear"
                variant="standard"
                margin="dense"
                fullWidth
                label="Year original owner died"
                helperText={inheritedYearMessage}
                value={inheritedYear}
                onChange={(event: any) => setInheritedYear(event.target.value)}
              />
              <FormGroupSpacer/>
              {(assetType === AssetType.IRANonSpouseInheritedBefore2020 || assetType === AssetType.RothIRANonSpouseInheritedBefore2020) &&
                <TextFieldValidator
                  type="number"
                  validators={{required: assetType === AssetType.IRANonSpouseInheritedBefore2020 || assetType === AssetType.RothIRANonSpouseInheritedBefore2020, isInt: true, minValue: 1900, maxValue: currentYear}}
                  name="originalOwnerBirthYear"
                  variant="standard"
                  margin="dense"
                  fullWidth
                  label="Original Owner Birth Year"
                  value={originalOwnerBirthYear}
                  onChange={(event: any) => setOriginalOwnerBirthYear(event.target.value)}
                />
              }
            </Box>
          }
          <TextFieldValidator
            type="text"
            validators={{ required: true }}
            required
            name="owner"
            label="Owner"
            variant="standard"
            fullWidth
            autocompleteOptions={{
              freeSolo: false,
              sx:{width:"100%"},
              options: model.getPersonNicknames(!assetTypeDef || (!assetTypeDef.isIRA && !assetTypeDef.hasIncomeRider)),
              value: owner,
              onChange: (event: any, value: string, reason: any) => {
                setOwner(value)
              }
            }}
          />
          <Box display="flex" flexGrow={1} justifyContent="stretch">
            <TextFieldValidator
              type="string"
              validators={{ isMoney: true }}
              name="balance"
              variant="standard"
              margin="dense"
              fullWidth
              sx={{width:"50%"}}
              label={balanceLabel}
              value={balance}
              onChange={(event: any) => setBalance(event.target.value)}
            />
            <FormGroupSpacer/>
            <TextFieldValidator
              type="string"
              validators={{ required: false, minValue: 0 }}
              name="returnRate"
              variant="standard"
              margin="dense"
              fullWidth
              sx={{width:"50%"}}
              label="Return Rate %"
              helperText={`Default: ${numberToPercentFormat(Model.defaultGrowthRate, 2)}`}
              value={returnRate}
              onChange={(event: any) => setReturnRate(event.target.value)}
            />
          </Box>
        </Paper>
        <Box display="flex" justifyContent="space-between" pt={1}>
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <DialogButton variant="primary" type="submit" spacing="right" data-testid="asset-quick-save">
              Save
            </DialogButton>
            <DialogButton variant="secondary" type="button" onClick={handleClose} spacing="right" data-testid="asset-quick-cancel">
              Cancel
            </DialogButton>
          </Box>
        </Box>
      </FormValidator>
    )
  }

  const setIncomeValue = (incomeType: IncomeType, value: string, name?: string) => {
    const item = incomeDetails.find((holding: IncomeDetailEdit) => holding.incomeType === incomeType)
    if (!name) {
      name = (incomeUnits === AssetIncomeUnits.NominalDollars) ? "amount" : "rate"
    }
    if (item) {
      (item as any)[name] = value
      setIncomeDetails([...incomeDetails])
    }
  }

  const getIncomeValue = (incomeType: IncomeType, name?: string) => {
    const item = incomeDetails.find((holding: IncomeDetailEdit) => holding.incomeType === incomeType)
    if (!name) {
      name = (incomeUnits === AssetIncomeUnits.NominalDollars) ? "amount" : "rate"
    }
    if (item) {
      return (item as any)[name]
    } else {
      return ""
    }
  }

  const getAssetIncomeDetail = (incomeType: IncomeType) : AssetIncomeDetailInput => {
    const detail = incomeDetails.find((holding: IncomeDetailEdit) => holding.incomeType === incomeType)
    if (detail) {
      const balanceValue = moneyToNumberFormat(balance, 0) ?? 0
      let amount: number | null = moneyToNumberFormat(detail.amount, 0)
      let rate: number | null = percentToNumberFormat(detail.rate, 2)
      if (incomeUnits === AssetIncomeUnits.NominalDollars) {
        rate = balanceValue > 0 ? (amount / balanceValue) : 0
      } else {
        amount = Math.round(balanceValue * rate)
      }
      return {
        incomeType: detail.incomeType,
        amount: amount,
        rate: rate
      }
    } else {
      return {
        incomeType: incomeType,
        amount: 0,
        rate: 0
      }
    }
  }

  const formatAmount = (amount: number, units: AssetIncomeUnits) : string => {
    if (!isNumber(amount)) {
      return ""
    } else if (units === AssetIncomeUnits.NominalDollars) {
      return numberToMoneyFormat(amount, 0)
    } else {
      return numberToPercentFormat(amount, 2)
    }
  }

  const convertIncomeUnits = (units: AssetIncomeUnits) => {
    const bal = moneyToNumberFormat(balance)
    if (units === AssetIncomeUnits.NominalDollars) {
      // Convert from percentages to dollars
      incomeDetails.forEach((item: IncomeDetailEdit) => {
        const percent = percentToNumberFormat(item.rate)
        const amount = Math.round(bal * percent)
        if (amount > 0) {
          item.amount = numberToMoneyFormat(amount, 0)
        } else {
          item.amount = ""
        }
      })
    } else if (units === AssetIncomeUnits.NominalRate) {
      // Convert from dollars to percentages
      incomeDetails.forEach((item: IncomeDetailEdit) => {
        const amount = moneyToNumberFormat(item.amount)
        const percent = (bal > 0) ? amount / bal : 0
        if (percent > 0) {
          item.rate = numberToPercentFormat(percent, 2)
        } else {
          item.rate = ""
        }
      })
    }
    setIncomeDetails([...incomeDetails])
  }

  const renderIncomeDetails = () => {
    const isPercent = incomeUnits === AssetIncomeUnits.NominalRate
    const amountValidators = isPercent ? {required: false, isPercent: true} : {required: false, isMoney: true}
    const capitalGainsIncome = getIncomeValue(IncomeType.LongTermCapitalGainsOrLoss)
    const basisRequired = ((isPercent && percentToNumberFormat(capitalGainsIncome) > 0) || (!isPercent && moneyToNumberFormat(capitalGainsIncome) > 0))

    return (
      <Paper elevation={1} sx={{m:0, mt:1,mb:2, px:2, py:1}}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            {/*<FormControl*/}
            {/*  variant="standard"*/}
            {/*  sx={{width:"100%"}}>*/}
            {/*  <InputLabel htmlFor="state-required">Income Entry Type</InputLabel>*/}
            {/*  <Select*/}
            {/*    native*/}
            {/*    value={incomeUnits}*/}
            {/*    variant="standard"*/}
            {/*    size="small"*/}
            {/*    sx={{fontSize: 16}}*/}
            {/*    margin="dense"*/}
            {/*    fullWidth*/}
            {/*    onChange={(event: any) => {*/}
            {/*      const units = parseInt(event.target.value)*/}
            {/*      setIncomeUnits(units)*/}
            {/*      convertIncomeUnits(units)*/}
            {/*    }}*/}
            {/*  >*/}
            {/*    <option key={AssetIncomeUnits.NominalDollars} value={AssetIncomeUnits.NominalDollars}>Nominal Dollar Amounts (from 1099 statement)</option>)*/}
            {/*    <option key={AssetIncomeUnits.NominalRate} value={AssetIncomeUnits.NominalRate}>Nominal Return Rate %</option>)*/}
            {/*  </Select>*/}
            {/*</FormControl>*/}
          </Grid>
          <Grid item xs={12}>
            <FormLabel sx={styles.formLabel}>{DetailDescriptions[incomeUnits]}</FormLabel>
          </Grid>

          {/* 1099-INT */}
          <Grid item xs={12}>
            {/*<FormLabel sx={styles.sectionLabel}>1099-INT</FormLabel>*/}
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={8}>
                <Typography variant="body2">Interest Income</Typography>
              </Grid>
              <Grid item xs={4}>
                <TextFieldValidator
                  type="string"
                  validators={amountValidators}
                  name="interestIncome"
                  variant="standard"
                  size="small"
                  margin="none"
                  styleProp={{maxHeight:"32px"}}
                  hiddenLabel
                  value={getIncomeValue(IncomeType.TaxableInterest)}
                  disabled={isLocked}
                  onChange={(event: any) => {
                    setIncomeValue(IncomeType.TaxableInterest, event.target.value)
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* 1099-DIV */}
          <Grid item xs={12}>
            {/*<FormLabel sx={styles.sectionLabel}>1099-DIV</FormLabel>*/}
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={8}>
                <Typography variant="body2">Qualified Dividends</Typography>
              </Grid>
              <Grid item xs={4}>
                <TextFieldValidator
                  type="string"
                  validators={amountValidators}
                  name="qualifiedDividends"
                  variant="standard"
                  size="small"
                  margin="none"
                  styleProp={{maxHeight:"32px"}}
                  hiddenLabel
                  value={getIncomeValue(IncomeType.QualifiedDividends)}
                  disabled={isLocked}
                  onChange={(event: any) => {
                    setIncomeValue(IncomeType.QualifiedDividends, event.target.value)
                  }}
                />
              </Grid>

              <Grid item xs={8}>
                <Typography variant="body2">Non-qualified Dividends</Typography>
              </Grid>
              <Grid item xs={4}>
                <TextFieldValidator
                  type="string"
                  validators={amountValidators}
                  name="ordinaryDividends"
                  variant="standard"
                  size="small"
                  margin="none"
                  styleProp={{maxHeight:"32px"}}
                  hiddenLabel
                  value={getIncomeValue(IncomeType.NonqualifiedDividends)}
                  disabled={isLocked}
                  onChange={(event: any) => {
                    setIncomeValue(IncomeType.NonqualifiedDividends, event.target.value)
                  }}
                />
              </Grid>

              <Grid item xs={8}>
                <Typography variant="body2">Capital Growth</Typography>
              </Grid>
              <Grid item xs={4}>
                <TextFieldValidator
                  type="string"
                  validators={amountValidators}
                  name="capitalGainDistributions"
                  variant="standard"
                  size="small"
                  margin="none"
                  styleProp={{maxHeight:"32px"}}
                  hiddenLabel
                  value={getIncomeValue(IncomeType.LongTermCapitalGainsOrLoss)}
                  disabled={isLocked}
                  onChange={(event: any) => {
                    setIncomeValue(IncomeType.LongTermCapitalGainsOrLoss, event.target.value)
                  }}
                />
              </Grid>

            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={1} alignItems="center">
            {/*  <Grid item xs={8}>*/}
            {/*    <Typography variant="h5">{*/}
            {/*      incomeUnits === AssetIncomeUnits.NominalRate ? "Total Income %" : "Total Income"*/}
            {/*    }</Typography>*/}
            {/*  </Grid>*/}
            {/*  <Grid item xs={4}>*/}
            {/*    <Typography variant="body2">{formatAmount(totalIncome, incomeUnits)}</Typography>*/}
            {/*  </Grid>*/}

              {/*{basisRequired &&*/}
                <>
                  <Grid item xs={8}>
                    <Typography variant="body2">Total Cost Basis</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <TextFieldValidator
                      type="string"
                      validators={{required: basisRequired, isMoney: true}}
                      name="basis"
                      variant="standard"
                      size="small"
                      margin="none"
                      hiddenLabel
                      value={incomeBasis}
                      disabled={isLocked}
                      onChange={(event: any) => {
                        setIncomeBasis(event.target.value)
                      }}
                    />
                  </Grid>
                </>
              {/*}*/}

              {/*<Grid item xs={8}>*/}
              {/*  <Typography variant="h5">Total Return Rate %</Typography>*/}
              {/*</Grid>*/}
              {/*<Grid item xs={4}>*/}
              {/*  <Typography variant="body2">{formatAmount(totalReturnRate, AssetIncomeUnits.NominalRate)}</Typography>*/}
              {/*</Grid>*/}
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    )
  }

  const renderIncomeHelp = () => {
    return (
      <Box display="flex" flexDirection="row" px={2}>
        <img src={ChatbotOpenImg} alt="chatbot" height={36} style={{borderRadius: "20px", marginRight: 8}}/>
        <Box>
          <Typography variant="body2" sx={{fontSize:13, mb:0.5}}>
            Entering taxable income details will enable calculating interest, dividends and capital gains for this Brokerage account which will appear in the Taxes and Retirement Optimizer views.
          </Typography>
          <Typography variant="body2" sx={{fontSize:13, mb:0.5}}>
            Estimate the rate of return or growth for each applicable type of income above.
            A corresponding income amount will be computed annually for each.
            Capital Gains are only realized as income when withdrawn.
            The Total Growth Rate will equal the sum of these rates.
          </Typography>
          <Typography variant="body2" sx={{fontSize:13}}>
            <b>Important:</b> If you have already added interest, dividends or capital gains Income items to the budget, you will need to remove them to avoid double taxation.
          </Typography>
        </Box>
      </Box>
    )
  }

  const renderLock = () => {
    if (isLocked) {
      let lockMsg = model.getLock(model.id, "*")
      if (!lockMsg) {
        lockMsg = model.getLock(asset.id, "*")
      }
      return (
        <Box display="flex" justifyContent="flex-start" alignItems="center" mt={1}>
          <LockIcon sx={{color: theme.palette.primary.main, mr:1}}/>
          <Typography variant="body2" color={theme.palette.primary.main}>
            {lockMsg}
          </Typography>
        </Box>
      )
    } else {
      return null
    }
  }


  const assetIncomeTooltip = <p>
    This will automatically calculate interest, dividends and capital gains on taxable assets (Checking, Savings, Brokerage, Money Market).<br/>
    <b>Important:</b> If you have already added interest, dividends or capital gains Income items to the budget, you will need to remove them to avoid double taxation.
    You will also need to update new Brokerage Liquid Asset options.
  </p>

  return (
    <ModelEditDialog title="Edit Asset" open={isOpen} size={size}
                     onCancel={handleClose}
                     onSave={!isLocked ? handleSave : undefined}
                     confirmDelete={false}
                     onDelete={!isLocked && onDelete ? handleDelete : undefined}
                     archived={archived}
                     confirmArchive={false}
                     onArchive={!isLocked && onArchive ? handleArchive : undefined}
    >
      {renderLock()}
      <Grid container spacing={2}>
        <Grid item sm={12} md={size === 'md' ? 6 : 12}>
          <TextFieldValidator
            margin="normal"
            name="description"
            label="Title"
            type="text"
            fullWidth
            variant="standard"
            required
            validators={{ required: true }}
            value={description}
            disabled={isLocked}
            onChange={(event: any) => setDescription(event.target.value)}
          />
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <TextFieldValidator
              type="string"
              validators={{ isMoney: true }}
              name="balance"
              variant="standard"
              margin="dense"
              sx={{width:"50%"}}
              label={balanceLabel}
              value={balance}
              disabled={isLocked}
              onChange={(event: any) => setBalance(event.target.value)}
            />
            <FormGroupSpacer/>
            <Box pt={1} width="50%">
              <DatePickerValidator
                name="balanceDate"
                label="Balance Date"
                value={balanceDate}
                format="MM-dd-yyyy"
                variant="standard"
                margin="dense"
                fullWidth
                required
                disabled
                onChange={(newValue: any) => {
                  setBalanceDate(newValue)
                }}
              />
            </Box>
          </Box>
          {assetCategory === AssetCategory.LiquidInvestableAssets &&
            <Box display="flex" flexDirection="row" justifyContent="stretch">
              {assetTypeDef?.useBalanceYStart &&
                <Box display="flex" width="50%">
                  <TextFieldValidator
                    type="string"
                    validators={{ required: false, isMoney: true }}
                    name="priorEoYBalance"
                    variant="standard"
                    margin="dense"
                    fullWidth
                    label={`${priorYear} End Balance`}
                    value={priorEoYBalance}
                    disabled={isLocked}
                    helperText="From the December 31 statement. If blank, it is estimated from today's balance."
                    onChange={(event: any) => setPriorEoYBalance(event.target.value)}
                  />
                </Box>
              }
              {assetTypeDef?.useBalanceYStart &&
                <FormGroupSpacer/>
              }
              {(assetTypeDef?.isTaxableIRA || assetTypeDef?.taxable) &&
              <Box display="flex" width={assetTypeDef?.useBalanceYStart ? "50%" : "100%"}>
                <TextFieldValidator
                  type="string"
                  validators={{ required: false, isMoney: true }}
                  name="withdrawalsYTD"
                  variant="standard"
                  margin="dense"
                  fullWidth
                  label="YTD Withdrawals"
                  value={withdrawalsYTD}
                  disabled={isLocked}
                  helperText="Year-to-date withdrawals not included in Asset Conversions or Expense Designated Assets"
                  onChange={(event: any) => setWithdrawalsYTD(event.target.value)}
                />
                </Box>
              }
            </Box>
          }
          {assetCategory === AssetCategory.LiquidInvestableAssets && assetType !== AssetType.Brokerage &&
            <TextFieldValidator
              type="text"
              validators={{ required: true }}
              required
              name="assetType"
              label="Account Type"
              variant="standard"
              autocompleteOptions={{
                freeSolo: false,
                options: listAssetTypes(),
                getOptionLabel: (option: number) => {
                  return(getAssetTypeLabel(option))
                },
                value: assetType,
                disabled: isLocked,
                onChange: (event: any, value: number, reason: any) => {
                  setAssetType(value)
                  const typeDef = getAssetTypeDef(value)
                  setAssetTypeDef(typeDef)
                  if (typeDef && (typeDef.isIRA || typeDef.hasIncomeRider) && owner && owner === Model.jointNickname) {
                    setOwner(undefined)
                  }
                  if (typeDef?.hasRMD || typeDef?.hasIncomeRider) {
                    setNetWorthExcluded(false)
                  }
                }
              }}
            />
          }
          {renderRiderFields()}
          {requireExtraFields &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <TextFieldValidator
                key={assetType}
                type="number"
                validators={{required: requireExtraFields, isInt: true, minValue: inheritedYearRange.min, maxValue: inheritedYearRange.max}}
                errorMessages={{minValue: inheritedYearMessage, maxValue: inheritedYearMessage}}
                name="inheritedYear"
                variant="standard"
                margin="dense"
                fullWidth
                label="Year original owner died"
                helperText={inheritedYearMessage}
                value={inheritedYear}
                disabled={isLocked}
                onChange={(event: any) => setInheritedYear(event.target.value)}
              />
              <FormGroupSpacer/>
              {(assetType === AssetType.IRANonSpouseInheritedBefore2020 || assetType === AssetType.RothIRANonSpouseInheritedBefore2020) &&
                <TextFieldValidator
                  type="number"
                  validators={{required: assetType === AssetType.IRANonSpouseInheritedBefore2020 || assetType === AssetType.RothIRANonSpouseInheritedBefore2020, isInt: true, minValue: 1900, maxValue: currentYear}}
                  name="originalOwnerBirthYear"
                  variant="standard"
                  margin="dense"
                  fullWidth
                  label="Original Owner Birth Year"
                  value={originalOwnerBirthYear}
                  disabled={isLocked}
                  onChange={(event: any) => setOriginalOwnerBirthYear(event.target.value)}
                />
              }
            </Box>
          }
          <Box display="flex" flexDirection="row" justifyContent="stretch">
            <Box display="flex" flexGrow={1}>
              <TextFieldValidator
                type="text"
                validators={{ required: true }}
                required
                name="owner"
                label="Owner"
                variant="standard"
                fullWidth
                autocompleteOptions={{
                  freeSolo: false,
                  sx:{width:"100%"},
                  options: model.getPersonNicknames(!assetTypeDef || (!assetTypeDef.isIRA && !assetTypeDef.hasIncomeRider)),
                  value: owner,
                  disabled: isLocked,
                  onChange: (event: any, value: string, reason: any) => {
                    setOwner(value)
                  }
                }}
              />
            </Box>
            <FormGroupSpacer/>
            <Box display="flex" flexGrow={1}>
              <TextFieldValidator
                type="text"
                validators={{required: true}}
                required
                name="risk"
                label="Risk"
                variant="standard"
                fullWidth
                autocompleteOptions={{
                  freeSolo: false,
                  sx:{width:"100%"},
                  options: Object.values(Risk).filter((v) => !isNaN(Number(v))),
                  getOptionLabel: (option: number) => {
                    return (humanizeString(Risk[option]))
                  },
                  value: risk,
                  disabled: isLocked,
                  onChange: (event: any, value: number, reason: any) => {
                    setRisk(value)
                  }
                }}
              />
            </Box>
          </Box>
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <MilestoneDateFieldValidator
              name="start"
              label="Acquisition Date"
              model={model}
              value={start}
              disabled={isLocked}
              width="50%"
              onChange={(value: string) => setStart(value)}
            />
            <FormGroupSpacer/>
            <Box width="50%" display="flex" flexDirection="column" justifyContent="flex-end">
              {managedCategory && isAdvisorOrAdmin && isClientAccount &&
                <FormControlLabel control={
                  <Switch checked={managed} disabled={isLocked}
                          onChange={(event: any) => {
                            setManaged(event.target.checked)
                          }}
                  />}
                  label={<span style={{fontSize: 14}}>Managed&nbsp;Asset</span>}
                  sx={{height:24}}
                />
              }
              {!assetTypeDef?.hasRMD && !assetTypeDef?.hasIncomeRider &&
                <>
                  <FormControlLabel control={
                    <Switch checked={netWorthExcluded} disabled={isLocked}
                            onChange={(event: any) => {
                              setNetWorthExcluded(event.target.checked)
                            }}
                    />}
                    label={<span style={{fontSize: 14}}>Exclude&nbsp;from&nbsp;Net&nbsp;Worth</span>}
                    sx={{height:24}}
                  />
                  {netWorthExcluded && assetCategory === AssetCategory.RealEstateAndProperty &&
                    <Typography variant="caption" sx={{display:"block", color:"text.secondary", fontStyle:"italic", pl:0.5}}>
                      Note: any mortgage or HELOC tied to this property will still count against Net Worth.
                    </Typography>
                  }
                </>
              }
            </Box>
            {/* Disabled for now */}
            {/*<MilestoneDateFieldValidator*/}
            {/*  name="end"*/}
            {/*  label="End Date"*/}
            {/*  model={model}*/}
            {/*  value={end}*/}
            {/*  width="50%"*/}
            {/*  onChange={(value: string) => setEnd(value)}*/}
            {/*/>*/}
          </Box>
          {assetCategory === AssetCategory.LiquidInvestableAssets &&
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <TextFieldValidator
                type="number"
                validators={{required: false}}
                name="withdrawalOrder"
                variant="standard"
                margin="dense"
                fullWidth
                label="Withdrawal Order"
                value={withdrawalOrder}
                placeholder={withdrawalOrderPlaceholder}
                disabled={isLocked || withdrawalOrderLock}
                helperText={withdrawalOrderLock ? "Withdrawal Strategy Set" : undefined}
                onChange={(event: any) => setWithdrawalOrder(event.target.value)}
              />
              <FormGroupSpacer/>
              {renderRateField()}
              {/*<TextFieldValidator*/}
              {/*  type="string"*/}
              {/*  validators={{required: false}}*/}
              {/*  name="returnRate"*/}
              {/*  variant="standard"*/}
              {/*  margin="dense"*/}
              {/*  fullWidth*/}
              {/*  label="Return Rate %"*/}
              {/*  value={returnRate}*/}
              {/*  onChange={(event: any) => setReturnRate(event.target.value)}*/}
              {/*/>*/}
            </Box>
          }
          {assetCategory === AssetCategory.RealEstateAndProperty &&
            <TextFieldValidator
              type="string"
              validators={{required: false}}
              name="returnRate"
              variant="standard"
              margin="dense"
              label="Growth Rate %"
              value={returnRate}
              disabled={isLocked}
              onChange={(event: any) => setReturnRate(event.target.value)}
            />
          }
          {/*{assetIncomeEnabled && assetTypeDef?.taxable && assetCategory === AssetCategory.LiquidInvestableAssets &&*/}
          {/*  <MilestoneDateFieldValidator*/}
          {/*    name="start"*/}
          {/*    label="Reinvest Income Until"*/}
          {/*    model={model}*/}
          {/*    value={assetIncomeDate}*/}
          {/*    // width="100%"*/}
          {/*    helperText={!assetIncomeDate ? "Default: Latest Life expectancy" : undefined}*/}
          {/*    onChange={(value: string) => setAssetIncomeDate(value)}*/}
          {/*  />*/}
          {/*}*/}
          <TextFieldValidator
            margin="normal"
            name="notes"
            label="Notes"
            type="text"
            fullWidth
            variant="outlined"
            multiline={true}
            minRows={6}
            maxRows={6}
            InputLabelProps={{shrink: true, sx:{mt:-1}}}
            InputProps={{
              sx: {
                fontSize: 14,
                lineHeight: 1.3,
                py: 1,
                mt: -1
              }
            }}
            validators={{ required: false }}
            value={notes}
            disabled={isLocked}
            onChange={(event: any) => setNotes(event.target.value)}
          />
        </Grid>
        {assetIncomeEnabled && size === "md" &&
          <Grid item sm={12} md={6} sx={{}}>
            {assetType === AssetType.Brokerage &&
              <Box sx={{display:"flex", flexDirection:"column", mt:"10px"}}>
                <Typography variant="h5" sx={{ml:1.5}}>Taxable Income</Typography>
                {renderIncomeDetails()}
                {renderIncomeHelp()}
              </Box>
            }
          </Grid>
        }
      </Grid>
    </ModelEditDialog>
  )
}

export default AssetEditDialog